import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSpc } from '../context/SpcContext';
import { CalendarDays, Trophy, Target, UserCheck, Bell, MessageCircle, Award, Share, SquarePlus, CheckCircle2 } from 'lucide-react';

const isIOSDevice = () => typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent);
const isStandaloneDisplay = () => typeof window !== 'undefined' && (window.navigator.standalone || window.matchMedia?.('(display-mode: standalone)').matches);

const BASE_STEPS = [
  {
    key: 'intro',
    title: "Welcome to the Clubhouse 👊",
    body: "You're officially in. This is where the whole club lives — sessions, challenges, rewards and a bit of banter. Let's do a quick 30-second tour.",
  },
  {
    key: 'card',
    icon: UserCheck,
    title: 'Your SPC Card',
    body: "Head to My SPC to find your Member Card — with your name, referral code and a 5-digit Member PIN. Give the coach your PIN at any session and they'll check you in. Every check-in earns SPC Points and can unlock achievements.",
  },
  {
    key: 'sessions',
    icon: CalendarDays,
    title: 'Book sessions',
    body: 'Tap Book to see what\'s on. SPC sessions run Tuesday, Thursday and Friday. Bookings are free unless a session says otherwise. Sessions full? Join the waitlist and we\'ll ping you the second a spot opens up.',
  },
  {
    key: 'challenges',
    icon: Target,
    title: 'Take on challenges',
    body: 'Head to More → Challenges. Whether it\'s a sunset run or a silly-o\'clock 5k, log your effort with a photo or a note. A coach approves each entry and awards points.',
  },
  {
    key: 'rewards',
    icon: Trophy,
    title: 'Earn & spend points',
    body: 'Earn points from attending, challenging yourself and referring friends, then spend them on partner perks in Benefits.',
  },
  // Games/Arcade parked — do not release yet. Re-add this step when ready.
  {
    key: 'ranks',
    icon: Award,
    title: 'Points & achievements',
    body: "Every session, challenge, referral and race adds points and can unlock a badge. Tap Points to see the club leaderboard and your unlocked achievements. Some legendary badges are hidden — earn them and confetti will fly.",
  },
  {
    key: 'whatsapp',
    isWhatsApp: true,
    icon: MessageCircle,
    title: 'Join the club chat',
    body: "The banter, plans and last-minute changes happen in the SPC WhatsApp group. Tap the green button below to jump in.",
  },
  {
    key: 'push',
    icon: Bell,
    title: 'Stay in the loop',
    body: "Turn on push notifications when your browser asks — you'll get pinged for session reminders, messages, waitlist promotions and challenge results. You can change your mind any time from your browser settings.",
    isIOSPush: true,
  },
];

function WhatsAppButton({ whatsappUrl }) {
  if (whatsappUrl) {
    return (
      <a className="btn welcome-whatsapp" href={whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="welcome-whatsapp-link">
        <span>💬</span> Join the SPC WhatsApp
      </a>
    );
  }
  return <p style={{ fontStyle: 'italic', color: '#a4a8ad', textAlign: 'center' }}>Your club WhatsApp link will appear here once an admin adds it in Club Settings.</p>;
}

export default function Welcome() {
  const { user, state, markWelcomeSeen } = useSpc();
  const [step, setStep] = useState(0);
  const [closing, setClosing] = useState(false);
  const needsIOSInstall = isIOSDevice() && !isStandaloneDisplay();

  const shouldShow = !!user && user.hasSeenWelcome === false && user.approvalStatus === 'approved';
  const currentStep = BASE_STEPS[step];
  const totalSteps = BASE_STEPS.length;
  const isLast = step === totalSteps - 1;
  const whatsappUrl = state?.settings?.whatsappUrl;
  const showIOSStep = currentStep.isIOSPush && needsIOSInstall;

  const finish = async () => {
    setClosing(true);
    await markWelcomeSeen();
  };

  if (!shouldShow) return null;

  return (
    <AnimatePresence>
      {!closing && (
        <motion.div
          className="welcome-backdrop"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="welcome-modal"
        >
          <motion.div
            className="welcome-card"
            initial={{ scale: 0.85, opacity: 0, y: 30 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 220, damping: 22 }}
          >
            <div className="welcome-progress">
              {BASE_STEPS.map((_, i) => (
                <span key={i} className={`welcome-dot ${i <= step ? 'active' : ''}`} />
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep.key}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25 }}
                className="welcome-body"
              >
                {currentStep.icon && (
                  <div className="welcome-icon">
                    {React.createElement(currentStep.icon, { size: 42 })}
                  </div>
                )}
                <h2 data-testid="welcome-title">{showIOSStep ? 'Add SPC to your Home Screen' : currentStep.title}</h2>
                {showIOSStep ? (
                  <div className="ios-install-steps" data-testid="welcome-ios-install-steps">
                    <p style={{ marginBottom: 10 }}>iPhone only delivers session reminders and messages once SPC is added to your Home Screen — it takes 10 seconds.</p>
                    <div className="list">
                      <div className="item"><span className="ios-step-num">1</span><div><strong>Tap the Share icon</strong><div className="meta">The square with an arrow, in Safari's toolbar</div></div><Share size={18} /></div>
                      <div className="item"><span className="ios-step-num">2</span><div><strong>Choose "Add to Home Screen"</strong><div className="meta">Scroll down the share sheet if you don't see it</div></div><SquarePlus size={18} /></div>
                      <div className="item"><span className="ios-step-num">3</span><div><strong>Tap "Add", then reopen from your Home Screen</strong><div className="meta">Push notifications only work from the installed app</div></div><CheckCircle2 size={18} /></div>
                    </div>
                  </div>
                ) : (
                  <p>{currentStep.body}</p>
                )}

                {currentStep.isWhatsApp && (
                  <div style={{ marginTop: 8 }}>
                    <WhatsAppButton whatsappUrl={whatsappUrl} />
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            <div className="welcome-actions">
              {step > 0 && (
                <button className="btn ghost" onClick={() => setStep(s => s - 1)} data-testid="welcome-back">Back</button>
              )}
              {!isLast ? (
                <>
                  <button className="btn ghost" onClick={finish} data-testid="welcome-skip">Skip tour</button>
                  <button className="btn" onClick={() => setStep(s => s + 1)} data-testid="welcome-next">Next</button>
                </>
              ) : (
                <button className="btn" onClick={finish} data-testid="welcome-finish">Let's go 👊</button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
